import { getAllAppointments } from "../services/appointmentService";
import {
  getAppointmentDate,
  isDateInRange,
  parseMonthYearBounds,
} from "./patientPeriodSummary";
import {
  getAppointmentStatusUpper,
  getTokenStatusUpper,
  isAppointmentCancelled,
  isAppointmentCompleted,
} from "./appointmentCancelUtils";

export const EMPTY_APPOINTMENT_STATUS_SUMMARY = {
  total: 0,
  completed: 0,
  cancelled: 0,
  noShow: 0,
};

let appointmentsListCache = null;
let appointmentsListInflight = null;

export async function loadAppointmentsListCached(forceRefresh = false) {
  if (!forceRefresh && Array.isArray(appointmentsListCache)) {
    return appointmentsListCache;
  }
  if (!forceRefresh && appointmentsListInflight) {
    return appointmentsListInflight;
  }
  appointmentsListInflight = getAllAppointments()
    .then((data) => {
      appointmentsListCache = Array.isArray(data) ? data : [];
      return appointmentsListCache;
    })
    .catch(() => {
      appointmentsListCache = [];
      return appointmentsListCache;
    })
    .finally(() => {
      appointmentsListInflight = null;
    });
  return appointmentsListInflight;
}

export function clearAppointmentsListCache() {
  appointmentsListCache = null;
  appointmentsListInflight = null;
}

export function classifyAppointmentStatusBucket(appointment) {
  if (!appointment) return "other";
  if (isAppointmentCancelled(appointment)) return "cancelled";

  const status = getAppointmentStatusUpper(appointment);
  if (status === "REJECTED") return "cancelled";

  const tokenStatus = getTokenStatusUpper(appointment);
  if (
    status === "NO_SHOW" ||
    status === "NOSHOW" ||
    status === "NO-SHOW" ||
    tokenStatus === "NO_SHOW" ||
    tokenStatus === "NOSHOW" ||
    tokenStatus === "NO-SHOW"
  ) {
    return "noShow";
  }

  if (isAppointmentCompleted(appointment)) return "completed";
  return "other";
}

export function summarizeAppointmentsInPeriod(appointments, month, year) {
  const { start, end } = parseMonthYearBounds(month, year);
  const summary = { ...EMPTY_APPOINTMENT_STATUS_SUMMARY };

  (appointments || []).forEach((apt) => {
    const aptDate = getAppointmentDate(apt);
    if (!isDateInRange(aptDate, start, end)) return;

    summary.total += 1;
    const bucket = classifyAppointmentStatusBucket(apt);
    if (bucket === "completed") summary.completed += 1;
    else if (bucket === "cancelled") summary.cancelled += 1;
    else if (bucket === "noShow") summary.noShow += 1;
  });

  return summary;
}

export async function fetchAppointmentStatusSummary(month, year, options = {}) {
  try {
    const list = await loadAppointmentsListCached(Boolean(options.forceRefresh));
    return summarizeAppointmentsInPeriod(list, month, year);
  } catch (error) {
    console.error("[appointmentPeriodSummary] Failed to fetch appointment summary:", error);
    return { ...EMPTY_APPOINTMENT_STATUS_SUMMARY };
  }
}

export function sumAppointmentStatusSummaries(list) {
  return (list || []).reduce(
    (acc, s) => ({
      total: acc.total + (Number(s?.total) || 0),
      completed: acc.completed + (Number(s?.completed) || 0),
      cancelled: acc.cancelled + (Number(s?.cancelled) || 0),
      noShow: acc.noShow + (Number(s?.noShow) || 0),
    }),
    { ...EMPTY_APPOINTMENT_STATUS_SUMMARY }
  );
}
